import { Box, Grid, Select, Text } from "@chakra-ui/react";
import Joi from "joi";
import { useCallback, useEffect } from "react";
import { FieldError } from "react-hook-form";

import {
  convenience as convenienceConfig,
  energyEfficiencyRating as energyEfficiencyConfig,
  furnished as furnishedConfig,
  houseConvenience as houseConvenienceConfig,
  propertyType as propertyTypeConfig,
} from "@ud/config/prodeje/config";
import { updateData } from "@ud/store/insertForm/insertSlice";
import IconArea from "components/Chakra/Icons/Area";
import IconDropdown from "components/Chakra/Icons/Dropdown";
import IconElektrina from "components/Chakra/Icons/Elektrina";
import IconPodlazi from "components/Chakra/Icons/Podlazi";
import IconPrislusenstvi from "components/Chakra/Icons/Prislusenstvi";
import IconSofa from "components/Chakra/Icons/Sofa";
import Input from "components/Chakra/Input";
import { NumericInput } from "components/Chakra/NumericInput";
import StepButtons from "components/InsertOfferWrapper/components/StepButtons";
import { useTracking } from "contexts/Tracking";
import { useAppDispatch, useAppSelector } from "hooks/reduxHooks";
import useForm, { OnFormSubmitType } from "hooks/useForm";
import { ClickableItems } from "../components/ClickableItems";
import SectionCards from "../components/SectionCards";
import { SectionTitle } from "../components/SectionTitle";

type IDescription = {
  buttonNextText?: string;
  buttonPrevText?: string;
  handleNextStep: () => void;
};

type CustomFormValue = "furnishing" | "usableArea";

type IFormValue = {
  furnishing: string;
  usableArea: string;
  estateArea: string;
  builtUpArea: string;
  totalFloors?: string;
  floorNumber?: string;
  conveniences: Array<string>;
  efficiency: string;
};

export const DescriptionFormOne: React.FC<IDescription> = ({
  buttonNextText,
  buttonPrevText,
  handleNextStep,
}) => {
  const { trackEvent } = useTracking();
  const dispatch = useAppDispatch();
  const insertReduxData = useAppSelector((e) => e.insertForm.data);
  const propertyType =
    insertReduxData.typeProperty ?? propertyTypeConfig.flat.id;

  const formSchema = Joi.object({
    furnishing: Joi.string().required(),
    usableArea: Joi.string(),
    estateArea: Joi.string().custom((value, helpers) => {
      if (!value && propertyType === propertyTypeConfig.house.id) {
        return helpers.error("any.invalid");
      }
    }),
    builtUpArea: Joi.string().allow(""),
    floorNumber: Joi.string().custom((value, helpers) => {
      if (
        typeof value === "undefined" &&
        propertyType === propertyTypeConfig.flat.id
      ) {
        return helpers.error("any.invalid");
      }
    }),
    totalFloors: Joi.string().allow(""),
    conveniences: Joi.array().items(Joi.string()),
    efficiency: Joi.string(),
  });

  const { handleSubmit, setValue, watch, registerWithError, errors } =
    useForm<IFormValue>(formSchema, {
      keepDataOnSuccess: true,
      defaultValues: {
        furnishing: insertReduxData.furnishing ?? undefined,
        usableArea: insertReduxData.usableArea ?? undefined,
        estateArea: insertReduxData.estateArea ?? undefined,
        builtUpArea: insertReduxData.builtUpArea ?? undefined,
        floorNumber: insertReduxData.floorNumber ?? undefined,
        totalFloors: insertReduxData.totalFloors ?? undefined,
        conveniences:
          propertyType === propertyTypeConfig.flat.id
            ? insertReduxData.conveniences ?? undefined
            : insertReduxData.houseConveniences ?? undefined,
        efficiency: insertReduxData.efficiency ?? energyEfficiencyConfig.g.id,
      },
    });

  const watchFurnishing = watch("furnishing");
  const watchUsableArea = watch("usableArea");
  const watchConveniences = watch("conveniences");
  const watchFloorNumber = watch("floorNumber");
  const watchEstateArea = watch("estateArea");

  const handleData: OnFormSubmitType<IFormValue> = ({
    usableArea,
    conveniences,
    furnishing,
    totalFloors,
    builtUpArea,
    efficiency,
  }) => {
    dispatch(
      updateData({
        data:
          propertyType === propertyTypeConfig.flat.id
            ? {
                usableArea: usableArea || undefined,
                conveniences: conveniences || [],
                floorNumber: watchFloorNumber || undefined,
                furnishing: furnishing || undefined,
                efficiency: efficiency || undefined,
                totalFloors: totalFloors || undefined,
              }
            : {
                usableArea: usableArea || undefined,
                furnishing: furnishing || undefined,
                houseConveniences: conveniences || [],
                estateArea: watchEstateArea || undefined,
                efficiency: efficiency || undefined,
                builtUpArea: builtUpArea || undefined,
              },
      }),
    );
    trackEvent({
      "dl.eCat": "offer_create",
      "dl.eAct": "bttn.clicked",
      "dl.eLab": "4. krok – Základní údaje",
    }),
      handleNextStep();
  };

  const checkIsBtnDisable = (): boolean => {
    const requiredArray: Array<string | Array<string> | undefined> = [
      watchFurnishing,
      watchUsableArea,
    ];

    requiredArray.push(
      propertyType === propertyTypeConfig.flat.id
        ? watchFloorNumber
        : watchEstateArea,
    );

    return requiredArray.some((item) => !item);
  };

  const handleSetValue = useCallback(
    (name: CustomFormValue, value: string) => {
      setValue(name, value);
    },
    [setValue],
  );

  useEffect(
    () =>
      trackEvent({
        "dl.eCat": "offer_create",
        "dl.eAct": "page.loaded",
        "dl.eLab": "4. krok – Základní údaje",
      }),
    [],
  );

  return (
    <form
      onSubmit={handleSubmit(handleData)}
      style={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        gap: 0,
        justifyContent: "space-between",
        width: "100%",
      }}
    >
      <Box textAlign="left">
        <SectionCards
          title="Vybavenost"
          setValue={handleSetValue}
          formWatch={watchFurnishing}
          nameValue="furnishing"
          items={furnishedConfig}
          numberOfColumns={{ mobile: 1, desktop: 2 }}
          cardStackCss={{ h: "unset" }}
          error={errors.furnishing}
          isRequired
          titlePaddingProps={{ pt: "0px", pb: "16px" }}
          icon={<IconSofa boxSize="16px" />}
        />
        <Grid
          gridTemplateColumns={{
            base: `1fr`,
            sm: `${
              propertyType === propertyTypeConfig.house.id
                ? "repeat(2, 1fr)"
                : "repeat(3, 1fr)"
            }`,
          }}
          rowGap={{ base: "0", md: "10px" }}
          columnGap={{ base: "10px", md: "10px" }}
        >
          <Box>
            <SectionTitle
              icon={<IconArea boxSize="16px" />}
              title="Užitní plocha"
              isRequired
            />
            <Input
              bg="ud-white"
              type="number"
              rightElement={<Text>m²</Text>}
              min="0"
              {...registerWithError("usableArea")}
            />
          </Box>
          {propertyType === propertyTypeConfig.house.id ? (
            <>
              <Box>
                <SectionTitle
                  icon={<IconArea boxSize="16px" />}
                  title="Plocha pozemku"
                  isRequired
                />
                <Input
                  bg="ud-white"
                  type="number"
                  min="0"
                  rightElement={<Text>m²</Text>}
                  {...registerWithError("estateArea")}
                />
              </Box>
              <Box>
                <SectionTitle
                  title="Zastavěná plocha"
                  icon={<IconArea boxSize="16px" />}
                />
                <Input
                  bg="ud-white"
                  type="number"
                  min="0"
                  rightElement={<Text>m²</Text>}
                  {...registerWithError("builtUpArea")}
                />
              </Box>
            </>
          ) : (
            <>
              <Box>
                <SectionTitle
                  title="Podlaží bytu"
                  isRequired
                  icon={<IconPodlazi boxSize="16px" />}
                />
                <NumericInput
                  {...registerWithError("floorNumber")}
                  extValue={
                    watchFloorNumber ? parseInt(watchFloorNumber) : null
                  }
                  setValueOutside={(value) => setValue("floorNumber", value)}
                  isZeroAllowed
                  min={-3}
                  max={30}
                  bg="ud-white"
                />
              </Box>
              <Box>
                <SectionTitle
                  icon={<IconPodlazi boxSize="16px" />}
                  title="Celkem podlaží"
                />
                <NumericInput
                  {...registerWithError("totalFloors")}
                  extValue={
                    insertReduxData.totalFloors
                      ? parseInt(insertReduxData.totalFloors)
                      : null
                  }
                  setValueOutside={(value) => setValue("totalFloors", value)}
                  min={0}
                  isZeroAllowed
                  max={30}
                  bg="ud-white"
                />
              </Box>
            </>
          )}
        </Grid>
        <SectionTitle
          icon={<IconPrislusenstvi boxSize="16px" />}
          title="Příslušenství"
        />
        <ClickableItems
          config={
            propertyType === propertyTypeConfig.flat.id
              ? convenienceConfig
              : houseConvenienceConfig
          }
          extValue={watchConveniences}
          setValue={(value) => setValue("conveniences", value)}
          error={errors.conveniences as FieldError}
        />
        <SectionTitle
          icon={<IconElektrina boxSize="16px" fillWith="ud-grey-600" />}
          title="Energetická náročnost"
          titlePaddingProps={{ pt: { base: "16px", md: "24px" }, pb: "16px" }}
          description={
            <Text variant="small_2" textAlign="end" flex={1}>
              Pokud ji neznáte, zvolte možnost G = neuvedeno.
            </Text>
          }
        />
        <Select
          backgroundColor="ud-white"
          {...registerWithError("efficiency")}
          icon={<IconDropdown />}
          iconSize="12px"
          defaultValue={energyEfficiencyConfig.g.translationId}
        >
          {Object.entries(energyEfficiencyConfig).map((item) => (
            <option key={item[0]} value={item[1].id}>
              {item[1].translationLong}
            </option>
          ))}
        </Select>
      </Box>
      <StepButtons
        buttonPrevText={buttonPrevText}
        buttonNextText={buttonNextText}
        checkIsBtnDisable={checkIsBtnDisable}
        mt={[null, "24px"]}
      />
    </form>
  );
};
